import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {defer, iif, Observable, of, race, Subject} from 'rxjs';
import {catchError, finalize, map, share, take, tap} from 'rxjs/operators';
import {extractListResponse, handleResponseError, ListResponse} from '../../common/api-helpers';
import {UserRole} from '../../enums/user-role';
import {CommonListFilterQueryParamsFactory} from '../../factories/common-list-query-params.factory';
import {ListFilterFactory} from '../../factories/list-filter.factory';
import {CommonListFilter} from '../../models/filters/common-list-filter';
import {AppConfigService} from '../../services/config.service';
import {AuthService} from '../auth/auth.service';
import {LayoutPreset} from './layout-preset';

const ALL_LAYOUTS_REQUEST_FILTER: CommonListFilter = ListFilterFactory.createWithSearchFields([]);

interface PendingRequest<T> {
    request$: Observable<T>;
    subject$: Subject<T>;
}

type LayoutsPendingRequest = PendingRequest<LayoutPreset[]>;

@Injectable()
export class LayoutPresetsService {

    private static createPendingRequest<T>(
        rawRequest$: Observable<T>,
        registerPendingRequest: (pendingRequest: PendingRequest<T>) => void,
        deletePendingRequest: () => void
    ): PendingRequest<T> {
        const subject$ = new Subject<T>();
        const request$ = race(
            rawRequest$,
            subject$
        ).pipe(
            take(1),
            finalize(deletePendingRequest),
            share()
        );

        const pendingRequest: PendingRequest<T> = {
            request$,
            subject$
        };

        registerPendingRequest(pendingRequest);

        return pendingRequest;
    }

    /**
     * Backoffice layout disabled for now because api return the same data as client side
     */
    private readonly layoutApiPathMap: { [userRole in UserRole]: string } = {
        [UserRole.ADMIN]:        undefined, // this.appConfigService.api['backofficeLayout'],
        [UserRole.TRADER]:       this.appConfigService.api['layout'],
        [UserRole.TRADER_ADMIN]: undefined // this.appConfigService.api['backofficeLayout'],
    };

    private layoutsPendingRequest: LayoutsPendingRequest;

    private cachedLayouts: LayoutPreset[];

    constructor(
        private httpClient: HttpClient,
        private authService: AuthService,
        private appConfigService: AppConfigService
    ) {}

    public getAllLayoutPresetsThroughCache(): Observable<LayoutPreset[]> {
        return this.cachedLayouts ? of(this.cachedLayouts) : this.getAllLayoutPresets();
    }

    private getAllLayoutPresets(): Observable<LayoutPreset[]> {
        const requestUrl = this.layoutApiPathMap[this.authService.userRole];
        const params = CommonListFilterQueryParamsFactory.createQueryParamsAsString(ALL_LAYOUTS_REQUEST_FILTER);
        const requestParams = new HttpParams({fromString: params});

        const layoutPresetRequest$ = defer(() =>
            this.httpClient.get<ListResponse<LayoutPreset>>(
                requestUrl,
                {params: requestParams}
            )
        );

        const request$ = iif(
            () => !!requestUrl,
            layoutPresetRequest$.pipe(
                map(extractListResponse),
                map(listResponse => listResponse.data),
                catchError(handleResponseError)
            ),
            of([])
        ).pipe(
            tap(layoutPresets => this.cachedLayouts = layoutPresets)
        );

        const pendingRequest = this.layoutsPendingRequest
            || LayoutPresetsService.createPendingRequest(
                request$,
                newPendingRequest => this.layoutsPendingRequest = newPendingRequest,
                () => this.layoutsPendingRequest = null
            );

        return pendingRequest.request$;
    }

}
